import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { gbp, toDisplay, toGbp, curSymbol } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Receipt, Plus, Download, Check, RotateCcw, Pencil, Trash2, Landmark } from "lucide-react";
import { CardHead, Field, FormDialog, ordinal } from "@/components/prohub/shared";
import BillImportDialog from "@/components/prohub/BillImportDialog";

const EMPTY = { name: "", amount: "", due_day: "", category: "Housing" };

function BillRow({ b, onToggle, onEdit, onDelete }) {
  return (
    <div data-testid={`pro-bill-${b.id}`} className="flex items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <p className={`text-sm font-medium truncate flex items-center gap-1.5 ${b.paid ? "line-through text-slate-500" : ""}`}>
          {b.name} {b.source === "bank" && <Landmark className="h-3 w-3 text-slate-500 shrink-0" />}
        </p>
        <p className="text-[11px] text-slate-500">{b.category} · due the {ordinal(b.due_day)}{!b.paid && ` · in ${b.days_until_due}d`}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="font-num text-sm font-semibold">{gbp(b.amount)}</span>
        <span data-testid={`pro-bill-status-${b.id}`} className={`text-[10px] px-1.5 py-0.5 rounded-full border ${b.paid ? "border-[#00C896]/30 bg-[#00C896]/10 text-[#00C896]" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`}>
          {b.paid ? (b.auto_paid ? "paid (bank)" : "paid") : "due"}
        </span>
        {!b.auto_paid && (
          <button data-testid={`pro-bill-toggle-${b.id}`} onClick={() => onToggle(b)} title={b.paid ? "Mark unpaid" : "Mark paid"} className="text-slate-500 hover:text-[#00C896] transition-colors">
            {b.paid ? <RotateCcw className="h-4 w-4" /> : <Check className="h-4 w-4" />}
          </button>
        )}
        <button data-testid={`pro-bill-edit-${b.id}`} onClick={() => onEdit(b)} className="text-slate-500 hover:text-white transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
        <button data-testid={`pro-bill-delete-${b.id}`} onClick={() => onDelete(b)} className="text-slate-500 hover:text-rose-400 transition-colors"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export default function BillsCard({ data, onChanged }) {
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const call = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); onChanged(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openForm = (b) => { setEditing(b || null); setForm(b ? { name: b.name, amount: String(toDisplay(b.amount)), due_day: String(b.due_day), category: b.category } : EMPTY); setOpen(true); };

  const save = async () => {
    setSaving(true);
    const payload = { name: form.name, amount: toGbp(form.amount), due_day: parseInt(form.due_day, 10), category: form.category };
    await call(() => (editing ? api.patch(`/pro-hub/bills/${editing.id}`, payload) : api.post("/pro-hub/bills", payload)), editing ? "Bill updated" : "Bill added");
    setSaving(false);
    setOpen(false);
  };

  const day = parseInt(form.due_day, 10);
  return (
    <div className="mm-glow-card p-6" data-testid="pro-bills-card">
      <CardHead icon={Receipt} title="Bills" sub={`${data.bills.length} tracked · ${gbp(data.bills_total_monthly)} a month`}
        action={<div className="flex gap-2">
          <Button data-testid="pro-import-bills" onClick={() => setImportOpen(true)} size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-white/5 hover:text-white"><Download className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Import</span></Button>
          <Button data-testid="pro-add-bill" onClick={() => openForm()} size="sm" className="rounded-full bg-[#00C896] hover:bg-[#00E5AC] text-[#04140D] font-semibold"><Plus className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Add bill</span></Button>
        </div>} />
      <div className="mt-3 divide-y divide-white/6">
        {data.bills.map((b) => (
          <BillRow key={b.id} b={b}
            onToggle={(x) => call(() => api.patch(`/pro-hub/bills/${x.id}`, { paid: !x.paid }))}
            onEdit={openForm}
            onDelete={(x) => call(() => api.delete(`/pro-hub/bills/${x.id}`), "Bill removed")} />
        ))}
        {data.bills.length === 0 && <p data-testid="pro-bills-empty" className="py-6 text-center text-sm text-slate-500">No bills yet. Import recurring payments from your bank or add one manually.</p>}
      </div>

      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Edit bill" : "Add a bill"} testid="pro-bill-modal" onSave={save} saving={saving}
        disabled={!form.name || !(parseFloat(form.amount) > 0) || !(day >= 1 && day <= 31)} saveLabel={editing ? "Save changes" : "Add bill"}>
        <Field label="Bill name" testid="pro-bill-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Council tax" />
        <div className="grid grid-cols-2 gap-3">
          <Field label={`Amount (${curSymbol()})`} testid="pro-bill-amount-input" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="145" />
          <Field label="Due day (1–31)" testid="pro-bill-day-input" type="number" min="1" max="31" value={form.due_day} onChange={(e) => setForm({ ...form, due_day: e.target.value })} placeholder="1" />
        </div>
        <div>
          <Label className="text-slate-300 text-sm">Category</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="pro-bill-category-select" className="mt-1.5 bg-[#0D0D0D] border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#14181D] border-white/10 text-white">
              {data.categories.map((c) => <SelectItem key={c} value={c} className="focus:bg-[#00C896]/20">{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </FormDialog>
      <BillImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={onChanged} />
    </div>
  );
}
